// @flow

import * as React from 'react';

import * as keyboardCodes from '@ta/common.keyboardCodes';
import { type ManagedTextAreaProps } from '@ta/input.text-area';

type Props = {|
  /** The input id */
  id?: string,

  /** The input form field name */
  name?: string,

  /** The initial value of the text input */
  value?: string,

  /** The number of visible text lines for the control. */
  rows?: number,

  /** The visible width of the text control, in average character widths. */
  cols?: number,

  /** Placeholder text to appear in the input when there is no value */
  placeholder?: string,

  /** Applies the disable flag to the <input /> */
  disabled?: boolean,

  /** If the input should automatically focus after initial render */
  focusOnMount?: boolean,

  /** If the input should automatically focus after updates */
  focusOnUpdate?: boolean,

  /**
   * The number of milliseconds to delay before triggering a typeahead search. This reduces the number of queries for
   * fast-typers. Defaults to 250ms.
   */
  searchDelay?: number,

  /**
   * Callback when the value changes.  This is not debounced
   */
  onChange?: (e: SyntheticKeyboardEvent<HTMLTextAreaElement>, newValue: string) => void,

  /**
   * Callback for when the typeahead query changes.  This is debounced
   */
  onQueryChange?: (newValue: string) => void,

  /**
   * Callback function for when the user first interacts with the component (i.e. focus or change). Is only called
   * once.
   */
  onInteractionStart?: (e: SyntheticEvent<HTMLTextAreaElement>) => void,

  /**
   * Callback for when the typeahead input gains focus.
   */
  onFocus?: (e: SyntheticEvent<HTMLTextAreaElement>) => void,

  /**
   * Callback for when the typeahead input loses focus.
   */
  onBlur?: (e: SyntheticEvent<HTMLTextAreaElement>) => void,

  /**
   * Callback to grab a ref to the text input
   */
  onRef?: (textInput: ?HTMLTextAreaElement) => mixed,

  /**
   * Type type of component to display.  e.g. ManagedTextComponent, UnderlinedTextComponent
   */
  inputComponentType: React.ComponentType<ManagedTextAreaProps>,

  /**
   * Class name(s) to be applied to the input component
   */
  inputComponentClassName?: string,

  /**
   * The line height of the text input (ex. '18px', '1em', etc.)
   */
  lineHeight?: string,

  /**
   * The max length for the input
   */
  maxLength?: number,
|};

/**
 * A text area used for typeahead which underlines the text as you type.
 */
class TypeaheadTextArea extends React.Component<Props> {
  static defaultProps = {
    name: undefined,
    value: undefined,
    placeholder: undefined,
    focusOnMount: false,
    focusOnUpdate: false,
    searchDelay: 250,
    onQueryChange: undefined,
    onInteractionStart: undefined,
    onFocus: undefined,
    onBlur: undefined,
    onRef: undefined,
  };

  onChange = (e: SyntheticKeyboardEvent<HTMLTextAreaElement>, newValue: string) => {
    if (this.props.onChange) {
      this.props.onChange(e, newValue);
    }

    // CX-1292: Return before clearTimeout on searchTimer since ENTER shouldn't stop results from loading.
    if (e && e.keyCode === keyboardCodes.KEY_ENTER) {
      return;
    }

    if (this.currentQuery === newValue) {
      // This can occur for onKeyUp events
      // Prevent it from triggering a query change
      return;
    }

    if (e) {
      // ignore some keys as they do not indicate a query change
      switch (e.keyCode) {
        case keyboardCodes.KEY_DOWN:
        case keyboardCodes.KEY_UP:
        case keyboardCodes.KEY_ESCAPE:
        case keyboardCodes.KEY_TAB:
        case keyboardCodes.KEY_CAPSLK:
        case keyboardCodes.KEY_LEFT:
        case keyboardCodes.KEY_RIGHT:
        case keyboardCodes.KEY_META:
        case keyboardCodes.KEY_SHIFT:
        case keyboardCodes.KEY_CTRL:
          return;
        default:
      }
    }

    // debounce multiple quick keystrokes and IME entry into a single onQueryChange event
    if (this.updateSearchTimeout) {
      clearTimeout(this.updateSearchTimeout);
    }
    this.currentQuery = newValue;

    this.updateSearchTimeout = setTimeout(() => {
      if (this.props.onQueryChange) {
        this.props.onQueryChange(newValue);
      }
    }, this.props.searchDelay);
  };

  updateSearchTimeout: TimeoutID;
  currentQuery: string;

  render() {
    const props: ManagedTextAreaProps = {
      id: this.props.id,
      name: this.props.name,
      value: this.props.value,
      className: this.props.inputComponentClassName,
      rows: this.props.rows,
      cols: this.props.cols,
      placeholder: this.props.placeholder,
      disabled: this.props.disabled,
      focusOnMount: this.props.focusOnMount,
      onFocus: this.props.onFocus,
      onBlur: this.props.onBlur,
      onChange: this.onChange,
      onKeyUp: this.onChange,
      onKeyDown: this.onChange,
      onCompositionEnd: this.onChange,
      onInteractionStart: this.props.onInteractionStart,
      onRef: this.props.onRef,
      maxLength: this.props.maxLength,
    };
    return React.createElement(this.props.inputComponentType, props);
  }
}

export default TypeaheadTextArea;
